import { useMemo, useState } from "react";
import Badge from "../components/Badge";
import ActivityMetricCard from "../components/ActivityMetricCard";
import KeySpendBarChart from "../components/KeySpendBarChart";
import {
  usePartnerRepStatus,
  usePartnerRepDashboard,
  usePartnerRepUsers,
  usePartnerRepUserKeys,
  usePartnerRepUserSummary,
  usePartnerRepUserTimeseries,
  usePartnerRepUserDashboard,
} from "../hooks/useApi";
import { formatTomanInt, formatNumber, formatDollar, formatToman } from "../utils/format";
import {
  USAGE_DURATIONS as DURATIONS,
  USAGE_DURATION_SECONDS as DURATION_SECONDS,
} from "../utils/usage";
import type { DashboardUsageData } from "../components/StackedAreaChart";

type UserRow = Record<string, unknown>;
type KeyRow = Record<string, unknown>;
type Usage = { tokens?: number; requests?: number; cost?: string | number };

function displayName(u: UserRow): string {
  const name = String(u.name || "");
  const family = String(u.family_name || "");
  const joined = [name, family].filter(Boolean).join(" ");
  return joined || String(u.email || u.phone || "بدون نام");
}

function dateLabel(iso: string | null | undefined): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("fa-IR");
}

const USAGE_TABS = [
  { key: "tokens", label: "توکن" },
  { key: "requests", label: "درخواست" },
  { key: "cost", label: "هزینه (تومان)" },
] as const;

function usageValue(usage: Usage | undefined, tab: string): string {
  if (!usage) return "۰";
  if (tab === "cost") {
    return formatTomanInt(usage.cost ?? 0);
  }
  return formatNumber(usage[tab as "tokens" | "requests"] ?? 0);
}

interface UsagePoint {
  time: string;
  end: string;
  total_tokens: number;
  total_cost: number;
  calculated_cost: number;
  requests: number;
}

function formatPointTime(iso: string, duration: string): string {
  const d = new Date(iso);
  if (duration === "1y") return d.toLocaleString("fa-IR", { month: "long" });
  if (duration === "1w" || duration === "1mo")
    return d.toLocaleString("fa-IR", { day: "numeric", month: "long" });
  return d.toLocaleString("fa-IR", { hour: "2-digit", minute: "2-digit" });
}

function pctChange(cur: number, prev: number): number | null {
  return prev > 0 ? ((cur - prev) / prev) * 100 : null;
}

export default function PartnersDashboard() {
  const { data: status, isLoading: statusLoading } = usePartnerRepStatus();
  const { data: dashboard, isLoading: dashLoading } = usePartnerRepDashboard();

  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("newest");
  const [sortDir, setSortDir] = useState("desc");

  const { data: usersData, isLoading: usersLoading, error: usersError } = usePartnerRepUsers({
    search: search.trim() || undefined,
    sort_by: sortBy,
    sort_dir: sortDir,
  });

  const [selectedUser, setSelectedUser] = useState<UserRow | null>(null);
  const [tab, setTab] = useState<(typeof USAGE_TABS)[number]["key"]>("tokens");
  const [userDuration, setUserDuration] = useState<string>("1w");

  const selectedUserId = selectedUser ? (selectedUser.user_id as number) : null;

  const { data: keysData, isLoading: keysLoading } = usePartnerRepUserKeys(selectedUserId);

  // User-scoped usage (drawer metric boxes + trend chart)
  const now = useMemo(() => new Date(), [selectedUserId]);
  const userWindowParams = useMemo(
    () => ({
      from_time: new Date(now.getTime() - DURATION_SECONDS[userDuration] * 1000).toISOString(),
    }),
    [now, userDuration]
  );
  const prevUserWindowParams = useMemo(
    () => {
      const to = new Date(Date.now() - DURATION_SECONDS[userDuration] * 1000);
      const from = new Date(to.getTime() - DURATION_SECONDS[userDuration] * 1000);
      return { from_time: from.toISOString(), to_time: to.toISOString() };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedUserId, userDuration]
  );

  const { data: userSummary, isLoading: userSummaryLoading } = usePartnerRepUserSummary(
    selectedUserId,
    userWindowParams
  );
  const { data: prevUserSummary } = usePartnerRepUserSummary(selectedUserId, prevUserWindowParams);
  const { data: userSeries, isLoading: userSeriesLoading } = usePartnerRepUserTimeseries(
    selectedUserId,
    userDuration
  );
  const { data: userChartData, isLoading: userChartLoading } = usePartnerRepUserDashboard(
    selectedUserId,
    "15d",
    "spend"
  );

  const userSeriesPoints = (userSeries || []) as UsagePoint[];

  const totalCost = Number(userSummary?.total_cost ?? 0);
  const prevCost = Number(prevUserSummary?.total_cost ?? 0);
  const totalRequests = Number(userSummary?.total_requests ?? 0);
  const prevRequests = Number(prevUserSummary?.total_requests ?? 0);
  const totalTokens = Number(userSummary?.total_tokens ?? 0);
  const prevTokens = Number(prevUserSummary?.total_tokens ?? 0);
  const totalCalc = Number(userSummary?.total_calculated_cost ?? 0);
  const prevCalc = Number(prevUserSummary?.total_calculated_cost ?? 0);

  const avgCost = totalTokens > 0 ? totalCalc / (totalTokens / 1_000_000) : 0;
  const prevAvgCost = prevTokens > 0 ? prevCalc / (prevTokens / 1_000_000) : 0;

  const spendSeries = userSeriesPoints.map((p) => ({ time: p.time, value: Number(p.total_cost) || 0 }));
  const requestsSeries = userSeriesPoints.map((p) => ({ time: p.time, value: Number(p.requests) || 0 }));
  const tokensSeries = userSeriesPoints.map((p) => ({ time: p.time, value: Number(p.total_tokens) || 0 }));
  const costSeries = userSeriesPoints.map((p) => {
    const tokens = Number(p.total_tokens) || 0;
    return {
      time: p.time,
      value: tokens > 0 ? (Number(p.calculated_cost) || 0) / (tokens / 1_000_000) : 0,
    };
  });

  const userLoading = userSummaryLoading && !userSummary;

  const users = useMemo(
    () => (usersData?.items as UserRow[] | undefined) ?? [],
    [usersData]
  );
  const keys = useMemo(
    () => (keysData as KeyRow[] | undefined) ?? [],
    [keysData]
  );

  if (statusLoading) {
    return (
      <div className="flex items-center justify-center py-24">
        <svg className="animate-spin h-8 w-8 text-primary" viewBox="0 0 24 24" fill="none">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
        </svg>
      </div>
    );
  }

  if (!status?.is_representative) {
    return (
      <div className="max-w-md mx-auto py-24 text-center">
        <div className="text-5xl mb-4">🏢</div>
        <h1 className="text-h2 font-semibold text-text-main mb-2">داشبورد همکاران</h1>
        <p className="text-body text-text-secondary">
          شما نماینده‌ی هیچ همکاری نیستید. در صورت نیاز با پشتیبانی آشا تماس بگیرید.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-h2 font-semibold text-text-main">داشبورد همکاران</h1>
        <p className="text-small text-text-tertiary mt-1">
          نماینده‌ی {dashLoading ? "..." : (dashboard?.company_name as string)}
        </p>
      </header>

      {/* Info cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card label="شرکت" value={dashLoading ? "..." : String(dashboard?.company_name ?? "")} icon={<IconCompany />} />
        <Card label="موجودی کیف پول" value={dashLoading ? "..." : `${formatTomanInt(dashboard?.balance)} تومان`} icon={<IconWallet />} />
        <Card label="تعداد کاربران" value={dashLoading ? "..." : formatNumber(dashboard?.user_count ?? 0)} icon={<IconUsers />} />
      </div>

      {/* Docs button */}
      <div className="flex justify-end">
        <a
          href={String(dashboard?.docs_url || "https://asha-ai.ir/partners/docs")}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-surface border border-line text-body text-text-main hover:bg-surface-hover transition-colors"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6" />
            <polyline points="15 3 21 3 21 9" />
            <line x1="10" y1="14" x2="21" y2="3" />
          </svg>
          مشاهده مستندات همکاران
        </a>
      </div>

      {/* Users table card */}
      <div className="bg-surface border border-line rounded-card">
        {/* Search + sort bar */}
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 p-4 border-b border-line">
          <div className="flex-1 relative">
            <svg className="absolute right-3 top-1/2 -translate-y-1/2 text-text-tertiary" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <circle cx="11" cy="11" r="8" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="جستجوی نام، نام خانوادگی، تلفن یا ایمیل..."
              className="w-full pr-9 pl-3 py-2 rounded-lg bg-surface-muted border border-line text-body text-text-main placeholder:text-text-tertiary focus:outline-none focus:border-primary"
            />
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="px-3 py-2 rounded-lg bg-surface-muted border border-line text-body text-text-main focus:outline-none focus:border-primary"
            >
              <option value="newest">جدیدترین</option>
              <option value="oldest">قدیمی‌ترین</option>
              <option value="balance">موجودی کیف پول</option>
              <option value="usage">مصرف کل</option>
            </select>
            <button
              type="button"
              onClick={() => setSortDir((d) => (d === "desc" ? "asc" : "desc"))}
              title="تغییر جهت مرتب‌سازی"
              className="px-3 py-2 rounded-lg bg-surface-muted border border-line text-text-secondary hover:bg-surface-hover transition-colors"
            >
              {sortDir === "desc" ? "⬇" : "⬆"}
            </button>
          </div>
        </div>

        {/* Table */}
        {usersLoading ? (
          <div className="flex items-center justify-center py-16">
            <svg className="animate-spin h-8 w-8 text-primary" viewBox="0 0 24 24" fill="none">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
          </div>
        ) : usersError ? (
          <div className="py-16 text-center text-body text-text-secondary">خطا در دریافت کاربران</div>
        ) : users.length === 0 ? (
          <div className="py-16 text-center text-body text-text-tertiary">کاربری یافت نشد</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-line">
                  <th className="text-right px-4 py-3 text-h3 text-text-secondary">نام</th>
                  <th className="text-right px-4 py-3 text-h3 text-text-secondary">ایمیل</th>
                  <th className="text-right px-4 py-3 text-h3 text-text-secondary">تلفن</th>
                  <th className="text-right px-4 py-3 text-h3 text-text-secondary">تاریخ ثبت‌نام</th>
                  <th className="text-right px-4 py-3 text-h3 text-text-secondary">موجودی</th>
                  <th className="text-right px-4 py-3 text-h3 text-text-secondary">مصرف (تومان)</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr
                    key={u.user_id as number}
                    onClick={() => {
                      setSelectedUser(u);
                      setTab("tokens");
                      setUserDuration("1w");
                    }}
                    className="cursor-pointer border-b border-line last:border-0 transition-colors hover:bg-surface-hover"
                  >
                    <td className="px-4 py-3.5 text-body text-text-main">{displayName(u)}</td>
                    <td className="px-4 py-3.5 text-body text-text-secondary" dir="ltr">
                      {String(u.email || "—")}
                    </td>
                    <td className="px-4 py-3.5 text-body text-text-secondary" dir="ltr">
                      {String(u.phone || "—")}
                    </td>
                    <td className="px-4 py-3.5 text-body text-text-secondary">
                      {dateLabel(u.created_at as string)}
                    </td>
                    <td className="px-4 py-3.5 text-body text-text-main font-number">
                      {formatTomanInt(u.wallet_balance as string | number)} تومان
                    </td>
                    <td className="px-4 py-3.5 text-body text-text-main font-number">
                      {formatTomanInt(u.total_usage_cost as string | number)} تومان
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Sliding drawer (left → middle) */}
      {selectedUser && (
        <div className="fixed inset-0 z-50" role="dialog">
          <div
            className="absolute inset-0 bg-black/25 backdrop-blur-sm animate-fade-in"
            onClick={() => setSelectedUser(null)}
          />
          <aside className="absolute top-0 bottom-0 left-0 w-full md:w-1/2 bg-surface-base border-r border-line shadow-[0_20px_60px_rgba(0,0,0,0.2)] flex flex-col overflow-hidden animate-drawer-left">
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-line shrink-0">
              <div>
                <h3 className="text-h3 font-semibold text-text-main">{displayName(selectedUser)}</h3>
                <p className="text-small text-text-tertiary mt-0.5" dir="ltr">
                  {String(selectedUser.email || "")} · {String(selectedUser.phone || "")}
                </p>
              </div>
              <button
                onClick={() => setSelectedUser(null)}
                className="p-1.5 rounded-lg text-text-tertiary hover:bg-surface-hover transition-colors"
                aria-label="بستن"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-5 space-y-5">
              {/* Duration filter + metric boxes (user-scoped, like /usage) */}
              <div>
                <div className="flex items-center justify-between gap-3 mb-3">
                  <h4 className="text-h3 font-semibold text-text-main">مصرف کاربر</h4>
                  <select
                    value={userDuration}
                    onChange={(e) => setUserDuration(e.target.value)}
                    className="px-3 py-2 rounded-lg bg-surface-muted border border-line text-small text-text-main focus:outline-none focus:border-primary"
                  >
                    {DURATIONS.map((d) => (
                      <option key={d.value} value={d.value}>{d.label}</option>
                    ))}
                  </select>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                  <ActivityMetricCard
                    label="کل هزینه"
                    loading={userLoading}
                    data={spendSeries}
                    value={formatTomanInt(userSummary?.total_cost)}
                    unit="تومان"
                    changePercent={pctChange(totalCost, prevCost)}
                    formatValue={(v) => formatToman(v)}
                    formatTime={(iso) => formatPointTime(iso, userDuration)}
                  />
                  <ActivityMetricCard
                    label="درخواست‌ها"
                    loading={userLoading}
                    data={requestsSeries}
                    value={formatNumber(userSummary?.total_requests)}
                    changePercent={pctChange(totalRequests, prevRequests)}
                    formatValue={(v) => formatNumber(v)}
                    formatTime={(iso) => formatPointTime(iso, userDuration)}
                  />
                  <ActivityMetricCard
                    label="حجم توکن"
                    loading={userLoading}
                    data={tokensSeries}
                    value={formatNumber(userSummary?.total_tokens)}
                    unit="توکن"
                    changePercent={pctChange(totalTokens, prevTokens)}
                    formatValue={(v) => formatNumber(v)}
                    formatTime={(iso) => formatPointTime(iso, userDuration)}
                  />
                  <ActivityMetricCard
                    label={
                      <>
                        میانگین هزینه <span dir="ltr">$/1M</span>
                      </>
                    }
                    loading={userLoading}
                    data={costSeries}
                    value={`$${formatDollar(avgCost)}`}
                    unit="/1M"
                    ltr
                    changePercent={pctChange(avgCost, prevAvgCost)}
                    formatValue={(v) => `$${formatDollar(v)}`}
                    formatTime={(iso) => formatPointTime(iso, userDuration)}
                  />
                </div>
              </div>

              {/* Usage tabs */}
              <div>
                <div className="flex items-center gap-1 bg-surface-muted border border-line rounded-xl p-1">
                  {USAGE_TABS.map((t) => (
                    <button
                      key={t.key}
                      onClick={() => setTab(t.key)}
                      className={`flex-1 px-3 py-2 rounded-lg text-small font-medium transition-all ${
                        tab === t.key
                          ? "bg-surface shadow-card text-primary"
                          : "text-text-secondary hover:text-text-main"
                      }`}
                    >
                      {t.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Keys list */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <h4 className="text-h3 font-semibold text-text-main">کلیدهای API</h4>
                  <span className="text-small text-text-tertiary font-number">{keys.length} کلید</span>
                </div>

                {keysLoading ? (
                  <div className="flex items-center justify-center py-10">
                    <svg className="animate-spin h-6 w-6 text-primary" viewBox="0 0 24 24" fill="none">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                  </div>
                ) : keys.length === 0 ? (
                  <div className="py-10 text-center text-body text-text-tertiary">هیچ کلیدی ساخته نشده است</div>
                ) : (
                  keys.map((k) => {
                    const usage = (k.usage as Usage | undefined);
                    const active = k.status === "active" || k.status === "ACTIVE";
                    return (
                      <div key={k.id as number} className="flex items-center justify-between gap-3 rounded-xl border border-line bg-surface-muted/50 px-4 py-3">
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="font-mono text-small text-text-secondary truncate" dir="ltr">
                              {String(k.key_prefix || "")}
                            </span>
                            <Badge variant={active ? "success" : "info"}>
                              {active ? "فعال" : "غیرفعال"}
                            </Badge>
                          </div>
                          <p className="text-small text-text-tertiary mt-1">
                            {String(k.name || "بدون نام")} · ساخت {dateLabel(k.created_at as string)}
                          </p>
                        </div>
                        <div className="text-left shrink-0">
                          <p className="text-small text-text-tertiary">{USAGE_TABS.find((t) => t.key === tab)?.label}</p>
                          <p className="text-h3 font-semibold text-primary font-number">
                            {usageValue(usage, tab)}
                          </p>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>

              {/* User-scoped detail usage (mirrors /api-keys drawer) */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-start">
                <div className="rounded-xl border border-line bg-surface p-4">
                  <h4 className="text-h3 font-semibold text-text-main mb-3">جزئیات مصرف</h4>
                  <div className="space-y-0.5">
                    <StatRow label="کل" value={userLoading ? "…" : `${userSummary?.total_cost ? formatTomanInt(userSummary.total_cost) : "۰"} تومان`} />
                    <StatRow label="امروز" value={userLoading ? "…" : `${userSummary?.today_cost ? formatTomanInt(userSummary.today_cost) : "۰"} تومان`} />
                    <StatRow label="این هفته" value={userLoading ? "…" : `${userSummary?.week_cost ? formatTomanInt(userSummary.week_cost) : "۰"} تومان`} />
                    <StatRow label="این ماه" value={userLoading ? "…" : `${userSummary?.month_cost ? formatTomanInt(userSummary.month_cost) : "۰"} تومان`} />
                  </div>
                </div>

                <div className="rounded-xl border border-line bg-surface p-4">
                  <div className="mb-3">
                    <p className="text-small text-text-tertiary mb-1">هزینه کل</p>
                    <p className="text-h1 font-semibold text-text-main font-number">
                      {userChartLoading ? "…" : formatTomanInt((userChartData as DashboardUsageData | undefined)?.summary?.spend)}
                      <span className="text-base text-text-tertiary font-normal mr-1">تومان</span>
                    </p>
                  </div>
                  <KeySpendBarChart data={userChartData as DashboardUsageData} loading={userChartLoading} />
                </div>
              </div>
            </div>
          </aside>
        </div>
      )}
    </div>
  );
}

function StatRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between py-1.5">
      <span className="text-small text-text-secondary">{label}</span>
      <span className="text-body font-number font-medium text-text-main">{value}</span>
    </div>
  );
}

function Card({ label, value, icon }: { label: string; value: string; icon: React.ReactNode }) {
  return (
    <div className="bg-surface border border-line rounded-card p-5 flex items-center gap-4">
      <div className="w-11 h-11 rounded-xl bg-primary-light text-primary flex items-center justify-center shrink-0">
        {icon}
      </div>
      <div className="min-w-0">
        <p className="text-small text-text-tertiary">{label}</p>
        <p className="text-h2 font-semibold text-text-main mt-0.5 truncate font-number">{value}</p>
      </div>
    </div>
  );
}

function IconCompany() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 21h18" />
      <path d="M5 21V5a2 2 0 012-2h6a2 2 0 012 2v16" />
      <path d="M15 9h4a2 2 0 012 2v10" />
      <path d="M9 7h2M9 11h2M9 15h2" />
    </svg>
  );
}

function IconWallet() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 12V7H5a2 2 0 01-2-2V4a2 2 0 012-2h14a1 1 0 011 1v2" />
      <rect x="3" y="7" width="18" height="14" rx="2" />
      <circle cx="17" cy="15" r="1" fill="currentColor" />
    </svg>
  );
}

function IconUsers() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 00-3-3.87" />
      <path d="M16 3.13a4 4 0 010 7.75" />
    </svg>
  );
}